// src/stores/macro.js
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useKeyboardStore } from './keyboard'
export const useMacroStore = defineStore('macro', () => {
    const keyboardStore = useKeyboardStore()
    const QUICK_LAUNCH_VIEW_STORAGE_KEY = 'layerQuickLaunchViewData'
    // 初始化默认的32个宏
    const initializeMacros = () => {
        // 从localstorage 读取保存的宏名称
        let savedMacroNames = {}
        try {
            const stored = localStorage.getItem('macroNames')
            if (stored) {
                savedMacroNames = JSON.parse(stored)
                console.log('从 localStorage 读取到保存的宏名称:', Object.keys(savedMacroNames).length)
            }
        } catch (error) {
            console.warn('读取 localStorage 宏名称失败:', error)
            // 如果解析失败，使用空对象，所有宏都用默认名称
            savedMacroNames = {}
        }
        const defaultMacros = []
        for (let i = 1; i <= 32; i++) {
            const macroId = `M${i}`
            const defaultName = `Macro${i}`
            // 判断逻辑：savedMacroNames 中是否存在这个 macroId
            const finalName = (macroId in savedMacroNames) ? savedMacroNames[macroId] : defaultName
            defaultMacros.push({
                id: macroId,
                name: finalName,
                bindKeys: [],
                actions: [],
                bindingMode: null, // 'macro' | 'quickLaunch' | null
                quickLaunch: null, // { type, value, layer, serial, uidList }
                uiState: {
                    panel: 'macro', // 'macro' | 'quickLaunch'
                    quickLaunchType: 'url', // 'url' | 'text' | 'file'
                    quickLaunchValue: ''
                },
                enabled: true,
                createdAt: Date.now(),
                updatedAt: Date.now()
            })
        }
        return defaultMacros
    }

    const macros = ref(initializeMacros())
    const bestTopBoxIndex = ref(3)
    const quickLaunchMode = ref(false)
    const quickLaunchType = ref('url')
    const quickLaunchValue = ref('')
    // const currentMacroId = ref('M1') // 默认选中第一个宏
    const currentMacroId = ref(localStorage.getItem('currentMacro') || 'M1') // 默认选中第一个宏
    // const currentBindKeys = ref([])
    const currentBindKeys = computed(() => {
        const currentMacro = macros.value.find(m => m.id
            === currentMacroId.value)
        return currentMacro ? (currentMacro.bindKeys ||
            []) : []
    })
    // 计算属性
    const currentMacroName = computed(() => {
        const currentMacro = macros.value.find(m => m.id === currentMacroId.value)
        return currentMacro ? currentMacro.name : ''
    })

    // 获取当前选中的宏
    const currentActions = computed(() => {
        const currentMacro = macros.value.find(m => m.id === currentMacroId.value)
        return currentMacro ? currentMacro.actions : []
    })

    function ensureMacroUiState(macro) {
        if (!macro || typeof macro !== 'object') return {
            panel: 'macro',
            quickLaunchType: 'url',
            quickLaunchValue: ''
        }
        if (!macro.uiState || typeof macro.uiState !== 'object') {
            macro.uiState = {
                panel: 'macro',
                quickLaunchType: 'url',
                quickLaunchValue: ''
            }
        }
        const panel = macro.uiState.panel === 'quickLaunch' ? 'quickLaunch' : 'macro'
        const typeRaw = String(macro.uiState.quickLaunchType || '').toLowerCase()
        const type = ['url', 'text', 'file'].includes(typeRaw) ? typeRaw : 'url'
        const value = String(macro.uiState.quickLaunchValue ?? '')
        macro.uiState.panel = panel
        macro.uiState.quickLaunchType = type
        macro.uiState.quickLaunchValue = value
        return macro.uiState
    }

    // 设置当前选中的宏
    function setCurrentMacro(macroId) {
        const macro = macros.value.find(m => m.id === macroId)
        if (macro) {
            currentMacroId.value = macroId
            const hadUiState = !!(macro.uiState && typeof macro.uiState === 'object')
            const ui = ensureMacroUiState(macro)
            if (!hadUiState && macro.bindingMode === 'quickLaunch' && macro.quickLaunch) {
                // 仅在历史数据首次补齐 uiState 时，用 quickLaunch 作为初始值
                ui.panel = 'quickLaunch'
                ui.quickLaunchType = String(macro.quickLaunch.type || 'url')
                ui.quickLaunchValue = String(macro.quickLaunch.value ?? '')
            }
            setQuickLaunchType(ui.quickLaunchType)
            setQuickLaunchValue(ui.quickLaunchValue)
            setQuickLaunchMode(ui.panel === 'quickLaunch')
            console.log(`选中宏: ${macro.name} (${macroId})`)
            return macro
        } else {
            throw new Error(`宏不存在: ${macroId}`)
        }
    }

    // 获取当前选中的宏
    function getCurrentMacro() {
        return macros.value.find(m => m.id === currentMacroId.value)
    }
    // 追加动作到现有宏
    function appendActions(macroId, newActions) {
        const macro = macros.value.find(m => m.id === macroId)
        if (!macro) {
            throw new Error('宏设置不存在')
        }

        macro.actions = [...macro.actions, ...newActions]
        macro.bindingMode = 'macro'
        macro.quickLaunch = null
        macro.updatedAt = Date.now()
        return macro
    }

    // 在指定位置插入动作到现有宏
    function insertActions(macroId, newActions, insertIndex = -1) {
        const macro = macros.value.find(m => m.id === macroId)
        if (!macro) {
            throw new Error('宏设置不存在')
        }

        // 如果insertIndex为-1或超出范围，则追加到末尾
        if (insertIndex === -1 || insertIndex >= macro.actions.length) {
            macro.actions = [...macro.actions, ...newActions]
        } else {
            // 在指定位置插入
            const beforeActions = macro.actions.slice(0, insertIndex)
            const afterActions = macro.actions.slice(insertIndex)
            macro.actions = [...beforeActions, ...newActions, ...afterActions]
        }

        macro.bindingMode = 'macro'
        macro.quickLaunch = null
        macro.updatedAt = Date.now()
        return macro
    }

    // 更新宏绑定按键
    function updateMacroBindKey(keyIndex, keycode) {
        const macroIndex = macros.value.findIndex(m => m.id === currentMacroId.value)
        if (macroIndex === -1) return
        const normalizedKeycode = Number(keycode)
        if (!Number.isInteger(normalizedKeycode) || normalizedKeycode < 0 || normalizedKeycode > 0xff) return
        removeBindKeysFromOtherMacros(currentMacroId.value, [normalizedKeycode])

        // 确保 bindKeys 数组存在
        if (!macros.value[macroIndex].bindKeys) {
            macros.value[macroIndex].bindKeys = []
        }

        let bindKeys = macros.value[macroIndex].bindKeys

        // 去重：移除其他位置相同的 keycode
        bindKeys = bindKeys.filter(key => Number(key) !== normalizedKeycode)

        // 扩展数组到指定长度，用现有的键码填充
        if (bindKeys.length < keyIndex) {
            // 如果长度不够，这里可能需要抛出错误或重新调整 keyIndex
            console.warn(`keyIndex ${keyIndex} 超出当前数组长度 ${bindKeys.length}`)
        }
        if (keyIndex === 99) {
            bindKeys.splice(keyIndex + 1, 0, normalizedKeycode)
        } else {
            // 在指定位置插入新的 keycode
            bindKeys.splice(keyIndex, 0, normalizedKeycode)
        }
        // 更新数组，确保没有 null 值
        macros.value[macroIndex].bindKeys = bindKeys.filter(key => key !== null && key !==
            undefined)
        macros.value[macroIndex].bindingMode = 'macro'
        macros.value[macroIndex].quickLaunch = null
        macros.value[macroIndex].updatedAt = Date.now()
        keyboardStore.updateKeyNameToMacro(macros.value[macroIndex].bindKeys, currentMacroId.value)
        return macros.value[macroIndex]
    }
    // 清空当前宏的动作
    function clearCurrentMacroActions() {
        const currentMacro = macros.value.find(m => m.id === currentMacroId.value)
        if (currentMacro) {
            currentMacro.actions = []
            currentMacro.updatedAt = Date.now()
            console.log(`已清空宏 ${currentMacro.name} 的所有动作`)
            return true
        }
        return false
    }

    // 删除对应的绑定按键
    function removeMacroBindKey(keyIndex) {
        const currentMacro = macros.value.find(m => m.id === currentMacroId.value)
        if (!currentMacro || !currentMacro.bindKeys || keyIndex < 0 || keyIndex >= currentMacro.bindKeys.length) {
            console.warn('无效的宏或索引')
            return
        }
        currentMacro.bindKeys.splice(keyIndex, 1)
    }

    // 删除指定索引的动作
    function removeAction(index) {
        const currentMacro = macros.value.find(m => m.id === currentMacroId.value)
        if (currentMacro && index >= 0 && index < currentMacro.actions.length) {
            // 删除指定位置的动作
            const removed = currentMacro.actions.splice(index, 1)[0]
            currentMacro.updatedAt = Date.now()
            console.log(`已从宏 ${currentMacro.name} 中删除动作:`, removed)
            return true
        }
        return false
    }

    function updateCurrentBindKeys(value) {
        const currentMacro = macros.value.find(m => m.id === currentMacroId.value)
        if (currentMacro) {
            const normalized = normalizeKeycodes(Array.isArray(value) ? value : [])
            removeBindKeysFromOtherMacros(currentMacroId.value, normalized)
            currentMacro.bindKeys = normalized
            // 如果当前是快捷启动交互态（包括首次进入该宏但还未写入 quickLaunch 对象），
            // 更新绑定键时保持快捷启动模式，避免 UI 跳回宏录制面板。
            if (currentMacro.bindingMode === 'quickLaunch' || quickLaunchMode.value || hasQuickLaunchInStorage(currentMacro.id)) {
                const layer = 0
                const serial = Number.parseInt(String(currentMacro.id || '').replace(/^\D+/, ''), 10) || 0
                if (!currentMacro.quickLaunch) {
                    currentMacro.quickLaunch = {
                        type: quickLaunchType.value || 'url',
                        value: quickLaunchValue.value || '',
                        layer,
                        serial,
                        uidList: []
                    }
                }
                currentMacro.bindingMode = 'quickLaunch'
                currentMacro.quickLaunch.uidList = normalized
            } else {
                currentMacro.bindingMode = 'macro'
                currentMacro.quickLaunch = null
            }
            currentMacro.updatedAt = Date.now()
            keyboardStore.updateKeyNameToMacro(normalized, currentMacroId.value)
        }
    }

    function setBestTopBoxIndex(value) {
        bestTopBoxIndex.value = Number(value) || 0
    }

    function setQuickLaunchMode(enabled) {
        const next = !!enabled
        quickLaunchMode.value = next
        const currentMacro = getCurrentMacro()
        if (currentMacro) {
            const ui = ensureMacroUiState(currentMacro)
            ui.panel = next ? 'quickLaunch' : 'macro'
        }
    }

    function setQuickLaunchType(value) {
        const next = String(value || '').toLowerCase()
        const allowed = ['url', 'text', 'file']
        quickLaunchType.value = allowed.includes(next) ? next : 'url'
        const currentMacro = getCurrentMacro()
        if (currentMacro) {
            const ui = ensureMacroUiState(currentMacro)
            ui.quickLaunchType = quickLaunchType.value
        }
    }

    function setQuickLaunchValue(value) {
        quickLaunchValue.value = String(value ?? '')
        const currentMacro = getCurrentMacro()
        if (currentMacro) {
            const ui = ensureMacroUiState(currentMacro)
            ui.quickLaunchValue = quickLaunchValue.value
        }
    }

    function normalizeKeycodes(value) {
        if (!Array.isArray(value)) return []
        const filtered = value
            .filter(item => item !== null && item !== undefined && item !== '')
            .map(item => Number(item))
            .filter(item => Number.isInteger(item) && item >= 0 && item <= 0xff)
        return [...new Set(filtered)]
    }

    function removeBindKeysFromOtherMacros(ownerMacroId, bindKeys) {
        if (!Array.isArray(bindKeys) || bindKeys.length === 0) return
        const normalizedSet = new Set(
            bindKeys
                .map(key => Number(key))
                .filter(key => Number.isInteger(key) && key >= 0 && key <= 0xff)
        )
        if (normalizedSet.size === 0) return
        const changedMacroIds = []
        macros.value.forEach((macro) => {
            if (macro.id === ownerMacroId || !Array.isArray(macro.bindKeys)) return
            const beforeLength = macro.bindKeys.length
            macro.bindKeys = macro.bindKeys.filter(key => !normalizedSet.has(Number(key)))
            if (beforeLength !== macro.bindKeys.length) {
                if (macro.quickLaunch && Array.isArray(macro.quickLaunch.uidList)) {
                    macro.quickLaunch.uidList = macro.quickLaunch.uidList.filter(uid => !normalizedSet.has(Number(uid)))
                    if (macro.quickLaunch.uidList.length === 0) {
                        macro.quickLaunch = null
                        macro.bindingMode = null
                    }
                }
                macro.updatedAt = Date.now()
                changedMacroIds.push(macro.id)
            }
        })
        changedMacroIds.forEach((macroId) => {
            const macro = macros.value.find(m => m.id === macroId)
            if (macro) {
                keyboardStore.updateKeyNameToMacro(macro.bindKeys || [], macro.id)
            }
        })
    }

    // 设置某个宏的快捷启动（与宏录制互斥）
    function setQuickLaunchForMacro(macroId, payload = {}) {
        const macro = macros.value.find(m => m.id === macroId)
        if (!macro) {
            throw new Error(`宏不存在: ${macroId}`)
        }
        const uidList = normalizeKeycodes(payload.uidList || payload.usageIds || [])
        const typeRaw = String(payload.type || '').toLowerCase()
        const type = ['url', 'text', 'file'].includes(typeRaw) ? typeRaw : 'url'
        const layerRaw = Number(payload.layer)
        const layer = Number.isInteger(layerRaw) ? Math.max(0, Math.min(3, layerRaw)) : 0
        const serialRaw = Number(payload.serial)
        const serial = Number.isInteger(serialRaw) ? serialRaw : Number.parseInt(String(macro.id).replace(/^\D+/, ''), 10) || 0
        const value = String(payload.value ?? '')

        removeBindKeysFromOtherMacros(macro.id, uidList)
        macro.bindKeys = uidList
        macro.actions = []
        macro.bindingMode = 'quickLaunch'
        macro.quickLaunch = {
            type,
            value,
            layer,
            serial,
            uidList
        }
        macro.updatedAt = Date.now()
        const ui = ensureMacroUiState(macro)
        ui.panel = 'quickLaunch'
        ui.quickLaunchType = type
        ui.quickLaunchValue = value

        if (macro.id === currentMacroId.value) {
            setQuickLaunchType(type)
            setQuickLaunchValue(value)
            setQuickLaunchMode(true)
        }

        keyboardStore.updateKeyNameToMacro(macro.bindKeys, macro.id)
        return macro
    }

    // 删除快捷启动中的某个按键绑定（可按层过滤）
    function removeQuickLaunchByUid(layer, uid, macroId = null) {
        const normalizedUid = Number(uid)
        const normalizedLayer = Number(layer)
        if (!Number.isInteger(normalizedUid)) return false

        const targets = macroId
            ? macros.value.filter(m => m.id === macroId)
            : macros.value

        let changed = false
        targets.forEach((macro) => {
            if (!Array.isArray(macro.bindKeys) || !macro.bindKeys.includes(normalizedUid)) return

            const quickLaunchLayer = Number(macro.quickLaunch?.layer)
            const needLayerMatch = Number.isInteger(normalizedLayer)
            if (needLayerMatch && Number.isInteger(quickLaunchLayer) && quickLaunchLayer !== normalizedLayer) return

            macro.bindKeys = macro.bindKeys.filter(key => key !== normalizedUid)
            if (macro.quickLaunch && Array.isArray(macro.quickLaunch.uidList)) {
                macro.quickLaunch.uidList = macro.quickLaunch.uidList.filter(key => key !== normalizedUid)
                if (macro.quickLaunch.uidList.length === 0) {
                    macro.quickLaunch = null
                    if (!macro.actions?.length) {
                        macro.bindingMode = null
                    }
                }
            }
            if (macro.bindingMode === 'quickLaunch' && (!macro.quickLaunch || macro.bindKeys.length === 0)) {
                macro.quickLaunch = null
                macro.bindingMode = null
            }
            macro.updatedAt = Date.now()
            changed = true
        })

        if (changed) {
            const currentMacro = getCurrentMacro()
            if (currentMacro) {
                keyboardStore.updateKeyNameToMacro(currentMacro.bindKeys || [], currentMacro.id)
            }
        }
        return changed
    }

    // 从所有宏中移除某个绑定按键（用于按键改回普通键时清理宏映射）
    function removeBindKeyFromAllMacros(uid) {
        const normalizedUid = Number(uid)
        if (!Number.isInteger(normalizedUid) || normalizedUid < 0 || normalizedUid > 0xff) return false

        let changed = false
        const changedMacroIds = []

        macros.value.forEach((macro) => {
            let macroChanged = false
            if (Array.isArray(macro.bindKeys) && macro.bindKeys.length > 0) {
                const beforeLength = macro.bindKeys.length
                macro.bindKeys = macro.bindKeys.filter(key => Number(key) !== normalizedUid)
                macroChanged = beforeLength !== macro.bindKeys.length
            }
            if (macro.quickLaunch && Array.isArray(macro.quickLaunch.uidList)) {
                const beforeUidLength = macro.quickLaunch.uidList.length
                macro.quickLaunch.uidList = macro.quickLaunch.uidList.filter(key => Number(key) !== normalizedUid)
                if (beforeUidLength !== macro.quickLaunch.uidList.length) {
                    macroChanged = true
                }
                if (macro.quickLaunch.uidList.length === 0) {
                    macro.quickLaunch = null
                    if (!macro.actions?.length) {
                        macro.bindingMode = null
                    }
                }
            }
            if (!macroChanged) return

            macro.updatedAt = Date.now()
            changed = true
            changedMacroIds.push(macro.id)
        })

        changedMacroIds.forEach((macroId) => {
            const macro = macros.value.find(m => m.id === macroId)
            if (macro) {
                keyboardStore.updateKeyNameToMacro(macro.bindKeys || [], macro.id)
            }
        })

        return changed
    }

    function removeImportedBindKeyFromAllMacros(uid) {
        const normalizedUid = Number(uid)
        if (!Number.isInteger(normalizedUid) || normalizedUid < 0 || normalizedUid > 0xff) return false

        let changed = false
        const changedMacroIds = []

        macros.value.forEach((macro) => {
            let macroChanged = false

            if (Array.isArray(macro.bindKeys) && macro.bindKeys.length > 0) {
                const beforeLength = macro.bindKeys.length
                macro.bindKeys = macro.bindKeys.filter(key => Number(key) !== normalizedUid)
                macroChanged = beforeLength !== macro.bindKeys.length
            }

            if (macro.quickLaunch && Array.isArray(macro.quickLaunch.uidList)) {
                const beforeUidLength = macro.quickLaunch.uidList.length
                macro.quickLaunch.uidList = macro.quickLaunch.uidList.filter(key => Number(key) !== normalizedUid)
                if (beforeUidLength !== macro.quickLaunch.uidList.length) {
                    macroChanged = true
                    if (macro.quickLaunch.uidList.length === 0) {
                        macro.quickLaunch = null
                        if (!macro.actions?.length) {
                            macro.bindingMode = null
                        }
                    }
                }
            }

            if (!macroChanged) return

            macro.updatedAt = Date.now()
            changed = true
            changedMacroIds.push(macro.id)
        })

        changedMacroIds.forEach((macroId) => {
            const macro = macros.value.find(m => m.id === macroId)
            if (macro) {
                keyboardStore.updateKeyNameToMacro(macro.bindKeys || [], macro.id)
            }
        })

        return changed
    }

    // 从 localStorage 读取快捷启动页面缓存
    function getQuickLaunchViewDataFromStorage() {
        try {
            const raw = localStorage.getItem(QUICK_LAUNCH_VIEW_STORAGE_KEY)
            if (!raw) return { 0: [], 1: [], 2: [], 3: [] }
            const parsed = JSON.parse(raw)
            return parsed && typeof parsed === 'object' ? parsed : { 0: [], 1: [], 2: [], 3: [] }
        } catch (error) {
            console.warn('[macro] 读取快捷启动页面缓存失败:', error)
            return { 0: [], 1: [], 2: [], 3: [] }
        }
    }

    // 判断某个宏在快捷启动缓存中是否有数据（按 serial=M序号 匹配）
    function hasQuickLaunchInStorage(macroId) {
        const serial = Number.parseInt(String(macroId || '').replace(/^\D+/, ''), 10)
        if (!Number.isInteger(serial) || serial <= 0) return false
        const source = getQuickLaunchViewDataFromStorage()
        for (let layer = 0; layer <= 3; layer++) {
            const layerList = Array.isArray(source[layer]) ? source[layer] : []
            if (layerList.some(item => Number(item?.serial) === serial)) {
                return true
            }
        }
        return false
    }

    // 将 layerQuickLaunchViewData 回灌到 macros：
    // 同步 bindKeys / bindingMode / quickLaunch，确保页面初始化后宏状态可直接使用
    function syncMacrosFromQuickLaunchViewData(viewData = null) {
        const source = viewData && typeof viewData === 'object'
            ? viewData
            : getQuickLaunchViewDataFromStorage()

        // 先清理历史 quickLaunch 模式，避免残留
        macros.value.forEach((macro) => {
            if (macro.bindingMode === 'quickLaunch') {
                macro.bindingMode = null
                macro.quickLaunch = null
                macro.bindKeys = []
                macro.updatedAt = Date.now()
            }
        })

        // 汇总为每个 macro 一条快捷启动数据（按 serial 对应 M序号）
        const macroPayloadMap = new Map()
        for (let layer = 0; layer <= 3; layer++) {
            const layerList = Array.isArray(source[layer]) ? source[layer] : []
            layerList.forEach((entry) => {
                const serial = Number(entry?.serial)
                const uid = Number(entry?.uid ?? entry?.keycode)
                if (!Number.isInteger(serial) || serial <= 0) return
                if (!Number.isInteger(uid) || uid < 0 || uid > 0xff) return

                const macroId = `M${serial}`
                const typeRaw = String(entry?.type || '').toLowerCase()
                const type = ['url', 'text', 'file'].includes(typeRaw) ? typeRaw : 'url'
                const value = String(entry?.value ?? '')

                let payload = macroPayloadMap.get(macroId)
                if (!payload) {
                    payload = {
                        serial,
                        layer,
                        type,
                        value,
                        uidList: []
                    }
                    macroPayloadMap.set(macroId, payload)
                }
                if (!payload.uidList.includes(uid)) {
                    payload.uidList.push(uid)
                }
                // 如存在冲突，后来的 type/value/layer 覆盖之前值
                payload.layer = layer
                payload.type = type
                payload.value = value
            })
        }

        // 应用到宏：这里会自动做跨宏 keycode 互斥
        macroPayloadMap.forEach((payload, macroId) => {
            if (macros.value.some(m => m.id === macroId)) {
                setQuickLaunchForMacro(macroId, payload)
            }
        })

        return macroPayloadMap.size
    }

    function setExportMacro(data, type) {
        // 网页导入宏不记录macroID 导入宏必须要去重
        let macroId = null
        if (type === 'equipment') {
            macroId = data.macroId
        } else {
            macroId = currentMacroId.value
        }
        const macroIndex = macros.value.findIndex(m => m.id === macroId)
        if (macroIndex === -1) return
        // setCurrentMacro(macroId)
        // const macroIndex = currentMacroId
        macros.value[macroIndex].actions = data.actions
        macros.value[macroIndex].bindKeys = normalizeKeycodes(data.bindKeys || [])
        macros.value[macroIndex].bindingMode = 'macro'
        macros.value[macroIndex].quickLaunch = null
        macros.value[macroIndex].updatedAt = Date.now()
        if (type === 'equipment') {
            keyboardStore.updateKeyNameToMacro(macros.value[macroIndex].bindKeys, data.macroId)
        } else {
            updateCurrentBindKeys(macros.value[macroIndex].bindKeys)
        }

    }

    function resetEffect() {
        // 循环去除bindKeys
        // currentBindKeys.value = []
        // const macro = getCurrentMacro()
        macros.value.forEach((item) => {
            item.bindKeys = []
        })
        // macro.bindKeys = []
    }

    // 保存所有宏的名称到 localStorage
    const saveMacroNames = () => {
        const macroNames = {}
        macros.value.forEach(macro => {
            macroNames[macro.id] = macro.name
        })
        try {
            localStorage.setItem('macroNames', JSON.stringify(macroNames))
            console.log('宏名称已保存到 localStorage')
        } catch (error) {
            console.error('保存宏名称失败:', error)
        }
    }
    // 从 localStorage 恢复宏的名称
    const restoreMacroNames = () => {
        try {
            const stored = localStorage.getItem('macroNames')
            if (stored) {
                const savedMacroNames = JSON.parse(stored)
                macros.value.forEach(macro => {
                    if (savedMacroNames[macro.id]) {
                        macro.name = savedMacroNames[macro.id]
                    }
                })
                console.log('宏名称已从 localStorage 恢复')
                return true
            }
        } catch (error) {
            console.error('恢复宏名称失败:', error)
        }
        return false
    }

    // 更新宏的名称并自动保存
    const updateMacroName = (macroId, newName) => {
        const macro = macros.value.find(m => m.id === macroId)
        if (macro) {
            macro.name = newName
            macro.updatedAt = Date.now()
            saveMacroNames()  // 自动保存
            return true
        }
        return false
    }
    return {
        // 数据
        macros,
        bestTopBoxIndex,
        quickLaunchMode,
        quickLaunchType,
        quickLaunchValue,
        currentMacroId,
        currentMacroName,
        currentActions,
        currentBindKeys,
        // 方法
        setExportMacro,
        setCurrentMacro,
        getCurrentMacro,
        appendActions,
        insertActions,
        clearCurrentMacroActions,
        removeAction,
        updateMacroBindKey,
        removeMacroBindKey,
        updateCurrentBindKeys,
        setBestTopBoxIndex,
        setQuickLaunchMode,
        setQuickLaunchType,
        setQuickLaunchValue,
        setQuickLaunchForMacro,
        removeQuickLaunchByUid,
        removeBindKeyFromAllMacros,
        removeImportedBindKeyFromAllMacros,
        syncMacrosFromQuickLaunchViewData,
        resetEffect,
        // 新增的持久化方法
        saveMacroNames,      // ← 保存
        restoreMacroNames,   // ← 恢复
        updateMacroName      // ← 更新单个名称
    }
})
